import { useEffect, useMemo } from "react"

import { formatNumber } from "accounting"
import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../../Components/Empty"
import Private from "../../../../Components/Private"
import Icon from "../../../../Icon"
import { formatCurrency } from "../../../../Utils/utils"
import { useProgramCount } from "../../hooks/useDeflectFilters"
import { DEFLECT_SECTION } from "../../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../../redux/deflectAnalytics.slice"
import { programIcon } from "../../utils/vocabulary"
import Section from "../Section"
import ProgramPanelSkeleton from "./ProgramPanelSkeleton"

import styles from "./programPanel.module.less"

// "By program": one row per card-network program with its logo, lookups + volume sub-label, a
// bar normalised to the busiest program, the lookup share and the match rate.
const ProgramPanel = ({ getData }) => {
  const { t } = useTranslation()
  const byProgram = useSelector(selectDeflectSection(DEFLECT_SECTION.BY_PROGRAM))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.BY_PROGRAM))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.BY_PROGRAM))
  const programCount = useProgramCount()

  useEffect(() => {
    getData(DEFLECT_SECTION.BY_PROGRAM)
  }, [getData])

  const rows = useMemo(() => {
    const result = byProgram?.result ?? []
    const max = result.reduce((a, row) => Math.max(a, row.lookups_count), 0) || 1
    return result.map((row) => ({ ...row, barWidth: (row.lookups_count / max) * 100 }))
  }, [byProgram])

  return (
    <Section
      title={t("By program")}
      tooltip={t("Lookup volume and match rate by card-network program.")}
    >
      {firstLoad ? (
        <ProgramPanelSkeleton count={programCount} />
      ) : (
        <Spin spinning={fetching}>
          {rows.length ? (
            <div className={styles.list}>
              {rows.map((r) => (
                <div className={styles.row} key={r.program}>
                  <span className={styles.brandIcon}>
                    <Icon type={programIcon(r.program)} />
                  </span>
                  <div className={styles.main}>
                    <div className={styles.line}>
                      <span className={styles.name}>{t(r.label)}</span>
                      <span className={styles.pct}>
                        <Private mask="∗∗∗">{`${Math.round(r.share)}%`}</Private>
                      </span>
                    </div>
                    <div className={styles.line}>
                      <span className={styles.sub}>
                        <Private>{formatNumber(r.lookups_count, 0, ",")}</Private>
                        {` ${t("lookups")} · `}
                        <Private>{formatCurrency(r.fees_amount)}</Private>
                      </span>
                      <span className={styles.win}>
                        {r.match_rate == null ? (
                          t("N/A")
                        ) : (
                          <Private>
                            {t("_rate_% match rate", { rate: Math.round(r.match_rate) })}
                          </Private>
                        )}
                      </span>
                    </div>
                    <div className={styles.bar}>
                      <div className={styles.barFill} style={{ width: `${r.barWidth}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyComponent type="no_data_available" />
          )}
        </Spin>
      )}
    </Section>
  )
}

export default ProgramPanel
